import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ModalWindow from '../ModalWindow';
import { ButtonType, InputType, ModalWindowTypes } from '../../../utils/@globalTypes';
import { setModalWindowData } from '../../../redux/reducers/pageSlice';
import {
  getExternalBooking,
  IntegrationSelectors,
  markExternalBookingViewed,
  updateExternalBooking,
} from '../../../redux/reducers/integrationSlice';
import { BookingSelectors } from '../../../redux/reducers/bookingSlice';
import { createObjectList, statusListOption } from '../../Booking/functions';
import BookingSectionTitle from '../../Booking/BookingSectionTitle';
import Input from '../../Input';
import SelectComponent from '../../SelectComponent';
import Button from '../../Button';
import { CloseIcon } from '../../../assets/icons';
import modalStyles from '../ShowReservationWindow/ShowReservationWindow.module.scss';
import bookingHeaderStyles from '../../Booking/BookingHeader/BookingHeader.module.scss';
import bookingContainerStyles from '../../Booking/BookingContainer/BookingContainer.module.scss';
import bookingObjectStyles from '../../Booking/BookingObjectBlock/BookingObjectBlock.module.scss';
import styles from './ExternalBookingWindow.module.scss';

type Props = { bookingId: number };

type ReadOnlyFieldProps = {
  id: string;
  title: string;
  value: string;
  type?: InputType;
};

const ReadOnlyField = ({ id, title, value, type = InputType.TEXT }: ReadOnlyFieldProps) => (
  <Input
    id={id}
    name={id}
    title={title}
    value={value || ''}
    placeholder="-"
    type={type}
    disabled
    isBooking
    onChange={() => undefined}
  />
);

const ExternalBookingWindow = ({ bookingId }: Props) => {
  const dispatch = useDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const [ownerComment, setOwnerComment] = useState('');
  const [bookingStatus, setBookingStatus] = useState<'awt' | 'cfm'>('awt');
  const booking = useSelector(IntegrationSelectors.getSelectedBooking);
  const isLoading = useSelector(IntegrationSelectors.getIsLoading);
  const isSaving = useSelector(IntegrationSelectors.getIsSaving);
  const error = useSelector(IntegrationSelectors.getError);
  const objects = useSelector(BookingSelectors.getChessObjects);

  useEffect(() => {
    dispatch(getExternalBooking(bookingId));
  }, [bookingId, dispatch]);

  useEffect(() => {
    if (booking?.id === bookingId && !booking.viewed) {
      dispatch(markExternalBookingViewed(bookingId));
    }
  }, [booking, bookingId, dispatch]);

  const close = () => dispatch(setModalWindowData({ type: ModalWindowTypes.CLOSE }));
  const currentBooking = booking?.id === bookingId ? booking : null;
  useEffect(() => {
    if (currentBooking) {
      setOwnerComment(currentBooking.owner_comment || '');
      setBookingStatus(currentBooking.booking_status);
    }
  }, [currentBooking]);
  const rentalObjectOptions = objects ? createObjectList(objects) || [] : [];
  const sourceName = currentBooking?.source_type === 'ical'
    ? 'iCalendar'
    : 'Менеджер каналов';
  const externalStatus = currentBooking?.sync_status === 'cancelled'
    ? 'Отменена во внешнем источнике'
    : currentBooking?.external_status || 'Не передан';
  const syncStatus = currentBooking?.sync_status === 'cancelled'
    ? 'Отменена'
    : currentBooking?.sync_status === 'conflict'
      ? 'Конфликт синхронизации'
      : 'Синхронизируется';
  const hasChanges = Boolean(currentBooking) && (
    ownerComment !== (currentBooking?.owner_comment || '') ||
    bookingStatus !== currentBooking?.booking_status
  );
  const saveChanges = () => {
    if (!currentBooking || !hasChanges || isSaving) return;
    dispatch(updateExternalBooking({
      id: currentBooking.id,
      data: {
        booking_status: bookingStatus,
        owner_comment: ownerComment,
      },
    }));
    setIsEdit(false);
  };

  return (
    <ModalWindow
      isLoading={!currentBooking && (isLoading || !error)}
      isFullCustom
      hideCross
      windowClassname={modalStyles.window}
    >
      {currentBooking ? (
        <>
          <header className={bookingHeaderStyles.container}>
            <div className={bookingHeaderStyles.title}>
              Внешняя бронь · {sourceName}
              {isEdit && (
                <div className={bookingHeaderStyles.titleAdd}>
                  (Режим редактирования)
                </div>
              )}
            </div>
            <div className={bookingHeaderStyles.btnPanel}>
              {!isEdit ? (
                <Button
                  title="Редактировать"
                  type={ButtonType.PRIMARY_SMALL}
                  onClick={() => setIsEdit(true)}
                />
              ) : (
                <>
                  <div className={styles.headerSave}>
                    <Button
                      title="Сохранить"
                      type={ButtonType.PRIMARY_SMALL}
                      onClick={saveChanges}
                      disabled={!hasChanges || isSaving}
                    />
                  </div>
                  <Button
                    title="Отмена"
                    type={ButtonType.SECONDARY_SMALL}
                    className={styles.cancelButton}
                    onClick={() => {
                      setOwnerComment(currentBooking.owner_comment || '');
                      setBookingStatus(currentBooking.booking_status);
                      setIsEdit(false);
                    }}
                  />
                </>
              )}
              <div className={bookingHeaderStyles.btnClose} onClick={close}>
                <CloseIcon />
              </div>
            </div>
          </header>
          <main className={`${bookingContainerStyles.mainContainer} ${styles.body}`}>
            <div className={styles.grid}>
              <section className={styles.objectSection}>
                <BookingSectionTitle
                  title="Информация об объекте"
                  created={moment(currentBooking.created_at).format('DD.MM.YYYY')}
                />
                <div className={styles.fieldGrid}>
                  <div>
                    <SelectComponent
                      title="Объект"
                      name="rental_object"
                      id="rental_object"
                      optionsList={rentalObjectOptions}
                      currentValue={String(currentBooking.rental_object)}
                      setSelecValue={() => undefined}
                      isBooking
                      isDisabled
                    />
                  </div>
                  <div>
                    <SelectComponent
                      title="Статус"
                      name="status"
                      id="status"
                      optionsList={statusListOption.filter((item) => item.value === 'awt' || item.value === 'cfm' || item.value === 'cld')}
                      currentValue={currentBooking.sync_status === 'cancelled' ? 'cld' : (isEdit ? bookingStatus : currentBooking.booking_status)}
                      setSelecValue={(value) => setBookingStatus(value as 'awt' | 'cfm')}
                      isBooking
                      isDisabled={!isEdit || currentBooking.sync_status === 'cancelled'}
                    />
                  </div>
                  <ReadOnlyField
                    id="check_in_date"
                    title="Дата заезда"
                    value={`${moment(currentBooking.check_in_date).format('DD.MM.YYYY')}${currentBooking.check_in_time ? `, с ${currentBooking.check_in_time}` : ''}`}
                  />
                  <ReadOnlyField
                    id="check_out_date"
                    title="Дата выезда"
                    value={`${moment(currentBooking.check_out_date).format('DD.MM.YYYY')}${currentBooking.check_out_time ? `, до ${currentBooking.check_out_time}` : ''}`}
                  />
                </div>
              </section>

              <section>
                <BookingSectionTitle title="Источник бронирования" />
                <div className={styles.fieldGrid}>
                  <ReadOnlyField id="source" title="Источник" value={sourceName} />
                  <ReadOnlyField id="external_status" title="Статус во внешнем источнике" value={externalStatus} />
                  <ReadOnlyField id="sync_status" title="Статус синхронизации" value={syncStatus} />
                  <ReadOnlyField id="external_uid" title="UID источника" value={currentBooking.external_uid} />
                </div>
              </section>

              <section>
                <BookingSectionTitle title="Личная информация" />
                <div className={styles.fieldGrid}>
                  <ReadOnlyField id="last_name" title="Фамилия" value={currentBooking.last_name || 'Не передано'} />
                  <ReadOnlyField id="first_name" title="Имя" value={currentBooking.first_name || 'Не передано'} />
                  <ReadOnlyField id="email" title="Электронная почта" value={currentBooking.email || 'Не передано'} />
                  <ReadOnlyField id="phone" title="Контактный телефон" value={currentBooking.phone || 'Не передано'} />
                </div>
              </section>

              <section className={styles.commentSection}>
                <BookingSectionTitle title="Комментарий владельца" />
                <div className={`${bookingObjectStyles.container} ${styles.commentContainer}`}>
                  {isEdit ? (
                    <Input
                      id="owner_comment"
                      name="owner_comment"
                      title="Комментарий владельца"
                      value={ownerComment}
                      placeholder="Введите текст"
                      type={InputType.TEXTAREA}
                      isBooking
                      className="comment"
                      onChange={(value) => setOwnerComment(value)}
                      maxLength={550}
                    />
                  ) : (
                    <div className={bookingObjectStyles.commentView}>
                      {currentBooking.owner_comment || 'Дополнительных комментариев нет'}
                    </div>
                  )}
                </div>
              </section>
            </div>
            {isEdit && (
              <div className={styles.mobileSave}>
                <Button
                  title="Сохранить"
                  type={ButtonType.PRIMARY_SMALL}
                  onClick={saveChanges}
                  disabled={!hasChanges || isSaving}
                />
              </div>
            )}
          </main>
        </>
      ) : error ? (
        <div className={styles.error} role="alert">
          <p>{error}</p>
          <Button
            title="Повторить"
            type={ButtonType.PRIMARY_SMALL}
            onClick={() => dispatch(getExternalBooking(bookingId))}
          />
          <Button title="Закрыть" type={ButtonType.SECONDARY_SMALL} onClick={close} />
        </div>
      ) : null}
    </ModalWindow>
  );
};

export default ExternalBookingWindow;
